/**
 * The automation's last steps, newest first: what each tick did and why.
 *
 * The server keeps the last 20 in memory (`auto/last-tick.ts`) and collapses a
 * run of identical quiet ticks into one line with a count, so "outside the
 * calling window" at every 30 seconds overnight reads as one line, not as a
 * wall. A line names the outcome, the reason in the automation's words, the
 * company a call went to, and what the tick did to the queue on the way
 * (companies taken out, companies added, client defect tasks filed).
 *
 * The history starts empty after the API restarts; the card says so rather
 * than looking broken.
 */
import { History } from "lucide-react";
import { useI18n } from "../../../../i18n/I18nProvider";
import type { AutomationStatus } from "../../../../lib/aiAutomation";
import { StatusPill } from "../../../ui";
import { Panel } from "../../marketing/shared";
import { fmtWhen, tickFacts, viewerTimeZone } from "./automationView";

export function AutomationHistoryCard({ status }: { status: AutomationStatus }) {
  const { t, locale } = useI18n();

  return (
    <Panel
      title={t("aiCalls.automation.history.title")}
      description={t("aiCalls.automation.history.intro")}
      icon={<History className="size-[18px]" strokeWidth={1.7} />}
    >
      {status.history.length === 0 ? (
        <p className="crm-sub mt-0 text-[13px]">{t("aiCalls.automation.history.empty")}</p>
      ) : (
        <ol className="-mx-[26px] divide-y divide-border">
          {status.history.map((report, index) => {
            const facts = tickFacts(report, status.campaigns);
            const counts: string[] = [];
            if (facts.skipped > 0) counts.push(t("aiCalls.automation.history.skipped", { n: facts.skipped }));
            if (facts.added > 0) counts.push(t("aiCalls.automation.history.added", { n: facts.added }));
            if (facts.tasks > 0) counts.push(t("aiCalls.automation.history.tasks", { n: facts.tasks }));
            if (facts.times > 1) counts.push(t("aiCalls.automation.history.times", { n: facts.times }));
            return (
              <li key={`${report.at}-${index}`} className="px-[26px] py-2.5">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <time dateTime={report.at} className="crm-sub mt-0 shrink-0 text-xs tabular-nums">
                    {fmtWhen(report.at, status.now, locale, status.settings.timezone, viewerTimeZone())}
                  </time>
                  <StatusPill tone={facts.tone}>{t(facts.outcomeKey)}</StatusPill>
                  {facts.test ? <StatusPill tone="info">{t("aiCalls.test")}</StatusPill> : null}
                  {facts.manual ? <span className="crm-sub mt-0 text-xs">{t(facts.triggerKey)}</span> : null}
                </div>
                <p className="mt-1 text-[13px]">
                  {facts.reasonKey ? t(facts.reasonKey) : facts.company || t("aiCalls.automation.history.noCompany")}
                  {facts.campaignName ? <span className="text-muted-foreground"> · {facts.campaignName}</span> : null}
                </p>
                {counts.length > 0 ? <p className="crm-sub mt-0.5 text-xs">{counts.join(" · ")}</p> : null}
              </li>
            );
          })}
        </ol>
      )}
    </Panel>
  );
}
